import { useState } from "react";
import { Star, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

/**
 * In-app testimonial submission modal. Authenticated users share a short
 * quote which lands in the `testimonials` collection with `status:pending`.
 * An admin approves it via the beheer panel, after which it starts
 * rotating on the landing page carousel.
 */
export default function ReviewSubmitModal({ open, onClose, category = "user" }) {
  const { t, lang } = useT();
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [alias, setAlias] = useState("");
  const [role, setRole] = useState("");
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (body.trim().length < 20) {
      toast.error(t("revErrShort"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/testimonials", { body: body.trim(), alias, role, city, rating, category, lang });
      toast.success(t("revThanks"));
      setBody(""); setAlias(""); setRole(""); setCity(""); setRating(5);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("revErrGeneric"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="review-modal"
      className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center px-4 pt-20"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#F9F8F6] border border-neutral-900 w-full max-w-lg shadow-xl">
        <div className="flex items-center justify-between border-b border-neutral-900 px-6 py-4">
          <p className="ed-eyebrow">{category === "partner" ? t("revTitlePartner") : t("revTitle")}</p>
          <button data-testid="review-modal-close" type="button" onClick={onClose} className="p-1 hover:bg-neutral-100">
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <p className="text-sm text-neutral-700 leading-relaxed">{category === "partner" ? t("revIntroPartner") : t("revIntro")}</p>

          <div>
            <p className="ed-label mb-2">{t("revRating")}</p>
            <div className="flex gap-1">
              {[1,2,3,4,5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`review-star-${n}`}
                  onClick={() => setRating(n)}
                  className="p-1"
                  aria-label={`${n} stars`}
                >
                  <Star size={22} strokeWidth={0} fill={n <= rating ? "#F5B301" : "#D6D3D0"} />
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="ed-label">{t("revBody")}</label>
            <textarea
              data-testid="review-body"
              rows={4}
              maxLength={400}
              placeholder={t("revBodyPh")}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="ed-input min-h-[6rem]"
            />
            <p className="text-[0.65rem] font-mono text-neutral-500 mt-1">{body.length} / 400</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="ed-label">{t("revAlias")}</label>
              <input data-testid="review-alias" className="ed-input" value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="Anne" />
            </div>
            <div>
              <label className="ed-label">{t("revRole")}</label>
              <input data-testid="review-role" className="ed-input" value={role} onChange={(e) => setRole(e.target.value)} placeholder="Marketeer" />
            </div>
            <div>
              <label className="ed-label">{t("revCity")}</label>
              <input data-testid="review-city" className="ed-input" value={city} onChange={(e) => setCity(e.target.value)} placeholder="Utrecht" />
            </div>
          </div>

          <p className="text-[0.65rem] font-mono text-neutral-500 leading-relaxed">{t("revMod")}</p>

          <div className="flex justify-end gap-2 pt-2 border-t border-neutral-200">
            <button data-testid="review-cancel" type="button" onClick={onClose} className="font-mono text-[0.65rem] uppercase tracking-[0.15em] border border-neutral-300 text-neutral-700 hover:border-neutral-900 hover:text-neutral-900 px-3 py-2">
              {t("wizCancel") || "Annuleren"}
            </button>
            <button data-testid="review-submit" type="button" onClick={submit} disabled={busy} className="ed-btn">
              {busy ? <><Loader2 size={13} strokeWidth={1.75} className="animate-spin" /> {t("revSending")}</> : t("revSubmit")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
